import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';

export default function LeadConsentPage() {
  const { token } = useParams();
  const location = useLocation();
  const [state, setState] = useState('loading');
  const [details, setDetails] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [preselected, setPreselected] = useState(null); // 'accepted' | 'declined' | null — cosmetic only, never auto-submits

  // ?decision=accepted|declined arrives from the email buttons, but must NOT
  // auto-submit on page load: mail security scanners / link-preview crawlers
  // (Microsoft Safe Links, Proofpoint, Gmail image/link proxies, etc.) fetch
  // every URL in an email automatically, which used to silently accept or
  // decline consent before the real recipient ever opened the message. We
  // only use it to pre-highlight the matching button; the POST only ever
  // fires from an explicit user click in respond().
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const prefill = params.get('decision');
    if (prefill === 'accepted' || prefill === 'declined') {
      setPreselected(prefill);
    }

    (async () => {
      try {
        const r = await fetch(`/api/introducer-leads/consent/${token}`);
        const d = await r.json();
        if (r.status === 410) {
          setState(d.status === 'expired' ? 'expired' : 'already_responded');
          setErrorMsg(d.error || '');
          return;
        }
        if (!r.ok) {
          setState('error');
          setErrorMsg(d.error || 'This consent request could not be found.');
          return;
        }
        setDetails(d);
        setState('ready');
      } catch {
        setState('error');
        setErrorMsg('Network error — please try again.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const respond = async (decision) => {
    setState('submitting');
    try {
      const r = await fetch(`/api/introducer-leads/consent/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision }),
      });
      const d = await r.json();
      if (!r.ok) {
        setState(d.status === 'expired' ? 'expired' : 'error');
        setErrorMsg(d.error || 'Something went wrong.');
        return;
      }
      setState(decision === 'accepted' ? 'accepted' : 'declined');
    } catch {
      setState('error');
      setErrorMsg('Network error — please try again.');
    }
  };

  const expiryDate = details?.expires_at
    ? new Date(details.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  const referredBy = details
    ? details.introducer_company
      ? `${details.introducer_name} at ${details.introducer_company}`
      : details.introducer_name
    : '';

  return (
    <div style={{ minHeight: '100vh', background: '#f0f9ff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ maxWidth: '520px', width: '100%' }}>

        {/* ── Dual-branded header ── */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          {details?.logo_url ? (
            <div style={{ display: 'inline-block', background: '#fff', borderRadius: '12px', padding: '12px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '10px' }}>
              <img src={details.logo_url} alt={details.broker_firm} style={{ height: '38px', maxWidth: '180px', objectFit: 'contain', display: 'block' }} />
            </div>
          ) : (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', background: '#0e1b2c', borderRadius: '12px', padding: '10px 20px', marginBottom: '10px' }}>
              <span style={{ color: '#10b981', fontSize: '18px', fontWeight: 800, letterSpacing: '-0.02em' }}>Broker</span>
              <span style={{ color: '#fff', fontSize: '18px', fontWeight: 800, letterSpacing: '-0.02em' }}>Connect</span>
            </div>
          )}
          {details?.broker_firm && (
            <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>{details.broker_firm}</div>
          )}
        </div>

        <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', padding: '36px', overflow: 'hidden' }}>

          {/* LOADING */}
          {state === 'loading' && (
            <div style={{ textAlign: 'center', color: '#6b7280', padding: '40px 0' }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
              <div>Loading your request…</div>
            </div>
          )}

          {/* SUBMITTING */}
          {state === 'submitting' && (
            <div style={{ textAlign: 'center', color: '#6b7280', padding: '40px 0' }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
              <div>Recording your response…</div>
            </div>
          )}

          {/* READY */}
          {state === 'ready' && details && (
            <>
              <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                <div style={{ fontSize: '28px', marginBottom: '10px' }}>🏠</div>
                <h1 style={{ fontSize: '20px', fontWeight: 800, color: '#0e1b2c', margin: '0 0 8px 0' }}>
                  Please verify your mortgage enquiry
                </h1>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: 1.6 }}>
                  Hi {details.client_name}, before we can progress your enquiry we need your permission to hold and contact you about your mortgage needs.
                </p>
              </div>

              {/* Details card */}
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                <div style={{ display: 'grid', gap: '14px' }}>
                  {[
                    ['Referred by', referredBy],
                    ['Mortgage firm', details.broker_firm],
                    ['Enquiry type', details.mortgage_type],
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap', paddingTop: '2px' }}>{label}</div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#0e1b2c', textAlign: 'right' }}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* What's shared */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#374151', marginBottom: '8px' }}>If you accept, we will hold:</div>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {['Your full name', 'Your email address', 'Your phone number', 'Details of your mortgage needs'].map(item => (
                    <li key={item} style={{ fontSize: '13px', color: '#4b5563', marginBottom: '5px' }}>{item}</li>
                  ))}
                </ul>
              </div>

              {/* GDPR note */}
              <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px', padding: '12px', marginBottom: '24px', fontSize: '12px', color: '#92400e', lineHeight: 1.6 }}>
                Your data will only be used to provide you with mortgage advice and support. You can withdraw consent at any time.
                {expiryDate && ` This request expires on ${expiryDate}.`}
              </div>

              {/* Buttons — clicking is the only thing that ever submits a decision */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <button
                  onClick={() => respond('declined')}
                  style={{ padding: '14px', border: preselected === 'declined' ? '2px solid #9ca3af' : '2px solid #e5e7eb', borderRadius: '10px', background: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 700, color: '#6b7280', transition: 'border-color .15s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#d1d5db'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = preselected === 'declined' ? '#9ca3af' : '#e5e7eb'}
                >
                  ✕ No thanks
                </button>
                <button
                  onClick={() => respond('accepted')}
                  style={{ padding: '14px', border: preselected === 'accepted' ? '2px solid #065f46' : 'none', borderRadius: '10px', background: '#10b981', cursor: 'pointer', fontSize: '14px', fontWeight: 700, color: '#fff', transition: 'background .15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#059669'}
                  onMouseLeave={e => e.currentTarget.style.background = '#10b981'}
                >
                  ✓ Yes, contact me
                </button>
              </div>
              {preselected && (
                <p style={{ textAlign: 'center', fontSize: '12px', color: '#9ca3af', marginTop: '14px' }}>
                  Please confirm your choice above — nothing is submitted until you click a button.
                </p>
              )}
            </>
          )}

          {/* ACCEPTED */}
          {state === 'accepted' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
              <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#065f46', marginBottom: '12px' }}>Thank you!</h2>
              <p style={{ fontSize: '14px', color: '#374151', lineHeight: 1.6 }}>
                Your enquiry has been passed to <strong>{details?.broker_firm || 'your mortgage broker'}</strong>. They will be in touch with you shortly.
              </p>
              <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '20px' }}>You can close this page.</p>
            </div>
          )}

          {/* DECLINED */}
          {state === 'declined' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>👍</div>
              <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0e1b2c', marginBottom: '12px' }}>No problem at all</h2>
              <p style={{ fontSize: '14px', color: '#374151', lineHeight: 1.6 }}>
                Your details will <strong>not</strong> be passed on and your enquiry has been closed. No further action is needed.
              </p>
              <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '20px' }}>You can close this page.</p>
            </div>
          )}

          {/* EXPIRED */}
          {state === 'expired' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏰</div>
              <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#92400e', marginBottom: '12px' }}>This request has expired</h2>
              <p style={{ fontSize: '14px', color: '#374151', lineHeight: 1.6 }}>
                The verification window has passed and this enquiry has been closed. If you'd still like mortgage advice, please contact your introducer directly.
              </p>
            </div>
          )}

          {/* ALREADY RESPONDED */}
          {state === 'already_responded' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>ℹ️</div>
              <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0e1b2c', marginBottom: '12px' }}>Already responded</h2>
              <p style={{ fontSize: '14px', color: '#374151', lineHeight: 1.6 }}>
                You've already responded to this request. No further action is needed.
              </p>
            </div>
          )}

          {/* ERROR */}
          {state === 'error' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
              <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#991b1b', marginBottom: '12px' }}>Something went wrong</h2>
              <p style={{ fontSize: '14px', color: '#374151', lineHeight: 1.6 }}>{errorMsg || 'Please check the link in your email and try again.'}</p>
            </div>
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '12px', color: '#9ca3af' }}>
          Powered by BrokerConnect &nbsp;·&nbsp; Your data is handled in accordance with GDPR
        </div>
      </div>
    </div>
  );
}
